import React from 'react';
import {
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
} from '@mui/material';
import type { PlanExecutionReport } from '../../../../../models/PlanExecutionTypes';
import { formatCurrency } from '../../../../../utils/CurrencyFormat';
import { sumCharges } from '../../../../../utils/planExecutionCalculations';

interface Props {
  report: PlanExecutionReport;
}

function SummaryCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.25 }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      )}
    </Paper>
  );
}

const PlanExecutionReportSection: React.FC<Props> = ({ report }) => {
  const c = report.totalCharges;
  const totalCharges = sumCharges(c);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" color="primary" gutterBottom>
        Execution report
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
        Buy vs sell summary with Indian equity charges (computed from saved transactions).
      </Typography>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <SummaryCard
            label="Shares bought"
            value={String(report.sharesBought)}
            sub={`Avg ₹${report.avgPurchasePrice.toFixed(2)}/share`}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <SummaryCard
            label="Purchase value"
            value={formatCurrency(report.totalPurchaseValue)}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <SummaryCard
            label="Shares sold"
            value={String(report.sharesSold)}
            sub={report.sharesSold > 0 ? `Avg ₹${report.avgSellPrice.toFixed(2)}/share` : 'No sells yet'}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <SummaryCard
            label="Sell proceeds"
            value={formatCurrency(report.totalSellProceeds)}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <SummaryCard label="Shares remaining" value={String(report.sharesRemaining)} />
        </Grid>
        <Grid item xs={6} md={3}>
          <SummaryCard
            label="Realized gain (pre-tax)"
            value={formatCurrency(report.realizedGainBeforeTax)}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <SummaryCard
            label="Est. STCG tax (15%)"
            value={formatCurrency(report.estimatedStcgTax)}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <SummaryCard
            label="Est. LTCG tax (10% post ₹1L)"
            value={formatCurrency(report.estimatedLtcgTax)}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" gutterBottom>
        Tax & charges breakdown (Indian equity delivery)
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Component</TableCell>
            <TableCell align="right">Amount (INR)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow><TableCell>STT</TableCell><TableCell align="right">{formatCurrency(c.stt)}</TableCell></TableRow>
          <TableRow><TableCell>Brokerage</TableCell><TableCell align="right">{formatCurrency(c.brokerage)}</TableCell></TableRow>
          <TableRow><TableCell>GST on brokerage</TableCell><TableCell align="right">{formatCurrency(c.gstOnBrokerage)}</TableCell></TableRow>
          <TableRow><TableCell>Exchange transaction charges</TableCell><TableCell align="right">{formatCurrency(c.exchangeTxnCharges)}</TableCell></TableRow>
          <TableRow><TableCell>SEBI turnover fees</TableCell><TableCell align="right">{formatCurrency(c.sebiTurnoverFees)}</TableCell></TableRow>
          <TableRow><TableCell>Stamp duty</TableCell><TableCell align="right">{formatCurrency(c.stampDuty)}</TableCell></TableRow>
          <TableRow sx={{ '& td': { fontWeight: 700 } }}>
            <TableCell>Total charges</TableCell>
            <TableCell align="right">{formatCurrency(totalCharges)}</TableCell>
          </TableRow>
          <TableRow sx={{ '& td': { fontWeight: 700, color: 'success.main' } }}>
            <TableCell>Net realized gain (after est. capital gains tax)</TableCell>
            <TableCell align="right">{formatCurrency(report.netRealizedGain)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Paper>
  );
};

export default PlanExecutionReportSection;
